// Decomposes a domain into the parts its scope can be clicked together from: a
// fixed anchor (the host it is carved out of and that can never be deselected),
// the subdomain labels between the anchor and the host, and the path segments.
//
// Depth semantics mirror how a scope reads left to right:
//   - subdomain: the LAST `subDepth` labels, those adjacent to the anchor.
//     `subdomainLabels` are in URL order, leftmost first.
//   - path: the FIRST `pathDepth` segments, those adjacent to the host.

// Scheme, "www.", query and hash never take part in a scope.
const splitUri = (raw) => {
	const cleaned = (raw || '')
		.trim()
		.replace(/^[a-z][a-z0-9+.-]*:\/\//i, '')
		.split(/[?#]/)[0]
	const [host, ...rest] = cleaned.split('/')
	return {
		host: (host || '')
			.toLowerCase()
			.replace(/^www\./, '')
			.replace(/\.$/, ''),
		segments: rest.filter(Boolean),
	}
}

const isUnder = (host, baseHost) =>
	!!baseHost && (host === baseHost || host.endsWith(`.${baseHost}`))

const labelsBelow = (host, anchor) =>
	host === anchor
		? []
		: host
				.slice(0, Math.max(0, host.length - anchor.length - 1))
				.split('.')
				.filter(Boolean)

const isPrefix = (short, long) =>
	short.length <= long.length && short.every((v, i) => long[i] === v)

// Subdomain depth counts from the anchor outwards, so a shorter selection is a
// suffix of the fuller label list.
const isSuffix = (short, long) =>
	short.length <= long.length &&
	short.every((v, i) => long[long.length - short.length + i] === v)

export const pathDepthOf = (uriOrUrl) => splitUri(uriOrUrl).segments.length

// Platforms whose scope is decided by determine-digital-property, not by
// clicking a URL apart: an Instagram or Facebook violation belongs to the
// profile/page the service resolved (instagram.com/<username>), and any scope
// assembled here would either be that same name or the bare platform domain —
// which is the generic property every one of these cases has to stay off.
const FIXED_SCOPE_HOSTS = ['instagram.com', 'facebook.com']

export const hasFixedScope = (uriOrUrl) => {
	const { host } = splitUri(uriOrUrl)
	return FIXED_SCOPE_HOSTS.some(
		(platform) => host === platform || host.endsWith(`.${platform}`),
	)
}

// Built around the domain's CURRENT `name`, widened by whatever the case URL
// reaches beyond it. Anchoring on the name is what lets the picker stay the
// only editor: the chips can always express what the field holds, so there is
// never a name it has to hand back to a text input.
//
// `baselineDomain` is the domain this one is carved out of — its host becomes
// the anchor, keeping the labels below it clickable. `lockBaselinePath` also
// fixes the path that domain covers, for a domain split off an existing one:
// that one may only go deeper, never broader. The domain the split is made
// *from* leaves it unlocked — narrowing or widening it is the point of editing.
//
// Returns null only when there is no host at all to anchor on.
export const decomposeDomainScope = ({
	name,
	sourceUrl,
	baselineDomain,
	lockBaselinePath,
}) => {
	const current = splitUri(name)
	const source = splitUri(sourceUrl)
	const base = splitUri(baselineDomain)

	const host = current.host || source.host
	if (!host) return null

	const anchored = isUnder(host, base.host)
	const anchor = anchored ? base.host : host

	// The URL only contributes labels and segments that extend the name; a URL
	// pointing somewhere else entirely leaves the name's own parts standing.
	const currentLabels = labelsBelow(host, anchor)
	const sourceLabels = isUnder(source.host, anchor)
		? labelsBelow(source.host, anchor)
		: []
	const subdomainLabels = isSuffix(currentLabels, sourceLabels)
		? sourceLabels
		: currentLabels

	const currentSegments = current.host ? current.segments : source.segments
	const pathSegments = isPrefix(currentSegments, source.segments)
		? source.segments
		: currentSegments

	const baselinePathDepth =
		lockBaselinePath && anchored && isPrefix(base.segments, pathSegments)
			? Math.min(base.segments.length, currentSegments.length)
			: 0

	return { anchor, subdomainLabels, pathSegments, baselinePathDepth }
}

export const assembleScope = (decomposition, subDepth, pathDepth) => {
	const { anchor, subdomainLabels, pathSegments } = decomposition
	const labels = subdomainLabels.slice(subdomainLabels.length - subDepth)
	const host = labels.length ? `${labels.join('.')}.${anchor}` : anchor
	const path = pathSegments.slice(0, pathDepth).join('/')
	return path ? `${host}/${path}` : host
}

// The picker is driven by the domain name itself rather than its own state, so
// the name has to map back onto chip depths. A decomposition built around that
// same name always maps; null is left for the name being emptied outright.
export const depthsForName = (decomposition, name) => {
	const { anchor, subdomainLabels, pathSegments, baselinePathDepth } =
		decomposition
	const { host, segments } = splitUri(name)

	let subDepth
	if (host === anchor) {
		subDepth = 0
	} else if (host.endsWith(`.${anchor}`)) {
		const labels = labelsBelow(host, anchor)
		if (!isSuffix(labels, subdomainLabels)) return null
		subDepth = labels.length
	} else {
		return null
	}

	if (segments.length > pathSegments.length) return null
	if (segments.length < baselinePathDepth) return null
	if (!isPrefix(segments, pathSegments)) return null

	return { subDepth, pathDepth: segments.length }
}
